import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Switch, Space, Tag, message, Tooltip } from "antd";
import { PlusOutlined, EyeOutlined } from "@ant-design/icons";
import Header from "../_shared/components/Header";
import {
  useGetStaffMembersQuery,
  useGetStaffProfilesQuery,
  useCreateStaffMemberMutation,
  useGetTasksQuery,
} from "../../../redux/api/scheduling";
import { useGetUsersQuery } from "../../../redux/api/users";
import StaffDetail from "./StaffDetail";

export default function Staff() {
  const { data, isLoading } = useGetStaffMembersQuery({});
  const { data: profiles } = useGetStaffProfilesQuery({});
  const { data: tasks } = useGetTasksQuery({});
  const { data: usersData } = useGetUsersQuery({});
  const [createMember] = useCreateStaffMemberMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const members = data?.results || data || [];
  const profileList = profiles?.results || profiles || [];
  const taskList = tasks?.results || tasks || [];
  const userList = usersData?.results || [];

  const handleSubmit = async (values: any) => {
    try {
      await createMember(values).unwrap();
      message.success("Staff member created");
      setModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error saving staff member");
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Profile",
      key: "profile",
      render: (_: any, r: any) => <Tag>{r.profile_initial} – {r.profile_name}</Tag>,
    },
    {
      title: "Tasks",
      key: "tasks",
      width: 220,
      render: (_: any, r: any) => {
        const taskTags = r.tasks_detail || [];
        if (taskTags.length === 0) return <span className="text-gray-400 italic text-xs">None</span>;
        const maxShow = 3;
        const shown = taskTags.slice(0, maxShow);
        const overflow = taskTags.length - maxShow;
        return (
          <Space size={[4, 4]} wrap>
            {shown.map((t: any) => (
              <Tag key={t.id} style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.name}
              </Tag>
            ))}
            {overflow > 0 && (
              <Tooltip title={taskTags.slice(maxShow).map((t: any) => t.name).join(", ")}>
                <Tag>+{overflow}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: "Scheduled Weeks",
      key: "deployed_summary",
      width: 260,
      render: (_: any, r: any) => {
        if (!r.deployed_summary) return <span className="text-gray-400 italic text-xs">None</span>;
        return <span className="text-xs">{r.deployed_summary}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_: any, record: any) => (
        <Button icon={<EyeOutlined />} size="small" onClick={() => setSelectedMemberId(record.id)}>
          View
        </Button>
      ),
    },
  ];

  if (selectedMemberId) {
    return (
      <div className="w-full p-4">
        <Button onClick={() => setSelectedMemberId(null)} className="mb-4">← Back to Staff List</Button>
        <StaffDetail memberId={selectedMemberId} onBack={() => setSelectedMemberId(null)} />
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <Header
        title="Staff"
        showOnClickButton
        buttonText="Add Staff Member"
        icon={<PlusOutlined />}
        handleOnClick={handleCreate}
      />

      <Table dataSource={members} columns={columns} loading={isLoading} rowKey="id" size="small" />

      <Modal
        title="Add Staff Member"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="user" label="Linked User" tooltip="Optional — link to a Django user account for ERP integration.">
            <Select
              allowClear
              placeholder="Select a user (optional)"
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
              }
              options={userList.map((u: any) => ({
                value: u.id,
                label: `${u.full_name || u.email} (${u.email})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="profile" label="Profile" rules={[{ required: true }]}>
            <Select options={profileList.map((p: any) => ({ value: p.id, label: `${p.name} (${p.initial})` }))} />
          </Form.Item>
          <Form.Item
            name="tasks"
            label="Qualified Tasks"
            tooltip="Tasks this staff member can perform. Auto-populated from profile defaults on creation. The scheduler only assigns work to staff members who have the task in this list."
          >
            <Select
              mode="multiple"
              options={taskList.map((t: any) => ({ value: t.id, label: `[${t.task_id}] ${t.name}` }))}
              placeholder="Leave empty to use profile defaults"
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="use_alternating_schedule" label="Alternating Schedule" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
